import type { FC } from 'react'
import { useSelector } from 'react-redux'

import { Avatar } from '@mui/material'

import { getUserGroupInitials, getUserGroupMembers } from './userGroups'
import { ArrowTooltip, AVATAR_BASE_SX, AVATAR_OWNER_SX, AVATAR_SIZE_SX } from '~/shared/ui'

interface UserGroupAvatarProps {
  id: number
  size?: 'small' | 'large'
  patientId?: number
  src?: string

  // `AVATAR_BASE_SX` hardcodes a 1rem margin that callers rely on; this lets one override it.
  avatarStyle?: Pick<React.CSSProperties, 'margin' | 'display' | 'order'>
}

const UserGroupAvatar: FC<UserGroupAvatarProps> = props => {
  const { id, size = 'large', patientId, src, avatarStyle } = props

  const me = useSelector(state => state.me)
  const providers = useSelector(state => state.providers)
  const assigneeGroups = useSelector(state => state.assigneeGroups)
  const users = useSelector(state => state.users.byId)

  let assigneeGroup = [...providers].find(user => user.assignee_group?.id == id)?.assignee_group

  if (!assigneeGroup && patientId) {
    const patientUser = users[patientId]
    if (patientUser?.assignee_group?.id == id) {
      assigneeGroup = patientUser.assignee_group
    }
  }
  if (!assigneeGroup && Array.isArray(assigneeGroups)) {
    assigneeGroup = assigneeGroups.find(group => group.id == id)
  }

  if (!assigneeGroup) {
    return null
  }

  const groupInitials = getUserGroupInitials(assigneeGroup)
  const groupName = getUserGroupMembers(assigneeGroup, providers)

  let owner: keyof typeof AVATAR_OWNER_SX = 'patient'
  if (id === me.assignee_group?.id) {
    owner = 'me'
  } else if (assigneeGroup.is_provider) {
    owner = 'provider'
  }

  return (
    <ArrowTooltip title={groupName}>
      <Avatar
        sx={[AVATAR_BASE_SX, AVATAR_SIZE_SX[size], AVATAR_OWNER_SX[owner]]}
        style={avatarStyle}
        src={src}
      >
        {groupInitials}
      </Avatar>
    </ArrowTooltip>
  )
}

export default UserGroupAvatar
